import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import { Loader2 } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useSpareMovementReport } from "@/features/spares/reportsApi";
import { useConfigOptions } from "@/features/configuration/api";

export const Route = createFileRoute("/_authenticated/reports/spare-movement-register")({
  component: SpareMovementRegisterReport,
});

const TYPES_FALLBACK = ["Opening", "Receipt", "Issue", "Issue to Work Order", "Return", "Return from Work Order", "Transfer Out", "Transfer In", "Adjustment"] as const;

function SpareMovementRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [movementType, setMovementType] = useState<string>("all");
  const [refType, setRefType] = useState<"all" | "wo" | "transfer" | "manual">("all");
  const [search, setSearch] = useState("");
  const { data: mtOpts } = useConfigOptions("spares", "movement_type");
  const types: readonly string[] = useMemo(() => {
    const active = (mtOpts ?? []).filter((r) => r.is_active).map((r) => r.value ?? r.label);
    return active.length > 0 ? active : TYPES_FALLBACK;
  }, [mtOpts]);
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data, isLoading } = useSpareMovementReport({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId ?? null,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
    movementType: movementType === "all" ? null : movementType,
    referenceType: refType,
    search: search || null,
  });
  const rows = data ?? [];

  const refNo = (r: typeof rows[number]) => r.work_order?.wo_number ?? r.invoice_ref ?? "—";
  const refTypeOf = (r: typeof rows[number]) => r.work_order_id ? "Work Order" : (r.type === "Transfer In" || r.type === "Transfer Out") ? "Transfer" : r.invoice_ref ? "Invoice" : "Manual";

  const exportRows = useMemo(() => () => rows.map((r) => ({
    Date: r.created_at,
    "Spare Code": r.spare?.code ?? "",
    "Spare Name": r.spare?.name ?? "",
    "Movement Type": r.type,
    Branch: r.branch?.name ?? "",
    Department: r.department?.name ?? "",
    "Store Location": r.store_location,
    Rack: r.rack_location ?? "",
    Batch: r.batch_no ?? "",
    Quantity: r.qty,
    "Reference Type": refTypeOf(r),
    "Reference No": refNo(r),
    "Performed By": r.performer?.full_name ?? r.performer?.email ?? "",
    Remarks: r.remarks ?? "",
  })), [rows]);

  return (
    <ReportShell
      id="R-36" title="Spare Movement Register"
      description="All spare stock movement transactions."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">Movement Type</Label>
            <Select value={movementType} onValueChange={setMovementType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Reference Type</Label>
            <Select value={refType} onValueChange={(v) => setRefType(v as typeof refType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="wo">Work Order</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="manual">Manual / Invoice</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code / name / WO / invoice" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Spare</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Dept</TableHead>
                <TableHead>Store / Rack / Batch</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Remarks</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{r.created_at.slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell><div className="font-medium">{r.spare?.name ?? "—"}</div><div className="font-mono text-xs text-muted-foreground">{r.spare?.code}</div></TableCell>
                    <TableCell>{r.type}</TableCell>
                    <TableCell>{r.branch?.name ?? "—"}</TableCell>
                    <TableCell>{r.department?.name ?? "—"}</TableCell>
                    <TableCell className="text-xs">{[r.store_location, r.rack_location, r.batch_no].filter(Boolean).join(" / ") || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.qty}</TableCell>
                    <TableCell className="text-xs">{refTypeOf(r)} · {refNo(r)}</TableCell>
                    <TableCell className="text-xs">{r.performer?.full_name ?? r.performer?.email ?? "—"}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs">{r.remarks ?? "—"}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">No spare records found for the selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} movements.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
